import { useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Layers, AlertCircle } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Page } from "@/components/layout/page";
import { useProject } from "@/components/project-context";
import { EmptyState } from "@/components/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Timestamp } from "@/components/ui/timestamp";
import { FilterPill } from "@/components/ui/filter-bar";
import { TableToolbar } from "@/components/ui/table-toolbar";
import { TableSearchInput } from "@/components/ui/table-search-input";
import { FilteredEmptyState } from "@/components/ui/filtered-empty-state";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  SortableTableHead,
} from "@/components/ui/table";
import { WalkthroughsTabs } from "@/components/gate/walkthroughs-tabs";
import {
  BASELINE_MODE_LABELS,
  GateStatusChip,
  shortSha,
  UncommittedChip,
} from "@/components/gate/gate-vocabulary";
import { severityTotal, type BuildSummary } from "@/components/gate/gate-data";

const ALL = "__all";

/**
 * Walkthroughs → Builds: one row per build (a group of runs made against
 * one version), with its stored gate result. Lists never recompute.
 */
export function ProjectBuildsPage() {
  const project = useProject();
  const [searchParams, setSearchParams] = useSearchParams();
  const environment = searchParams.get("env") ?? ALL;
  const branch = searchParams.get("branch") ?? ALL;
  const searchQuery = searchParams.get("search") ?? "";
  const sortColParam = searchParams.get("sort");
  const sortCol: "date" | "build" | "status" | "new" | "fixed" =
    sortColParam === "build" ||
    sortColParam === "status" ||
    sortColParam === "new" ||
    sortColParam === "fixed"
      ? sortColParam
      : "date";
  const sortDirParam = searchParams.get("dir");
  const sortDir: "ascending" | "descending" =
    sortDirParam === "ascending" ? "ascending" : "descending";

  const [cursor, setCursor] = useState<string | null>(null);
  const [pages, setPages] = useState<BuildSummary[][]>([]);

  const resetPaging = () => {
    setCursor(null);
    setPages([]);
  };

  const { data, isLoading, error, refetch } = trpc.build.list.useQuery({
    projectId: project.id,
    environment: environment === ALL ? null : environment,
    branch: branch === ALL ? null : branch,
    cursor,
  });
  const builds = [
    ...pages.flat(),
    ...((data?.items ?? []) as unknown as BuildSummary[]),
  ];

  const hasActiveFilters =
    environment !== ALL || branch !== ALL || searchQuery.trim().length > 0;

  const handleEnvironmentChange = (env: string) => {
    resetPaging();
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (env !== ALL) {
          next.set("env", env);
        } else {
          next.delete("env");
        }
        return next;
      },
      { replace: true },
    );
  };

  const handleBranchChange = (br: string) => {
    resetPaging();
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (br !== ALL) {
          next.set("branch", br);
        } else {
          next.delete("branch");
        }
        return next;
      },
      { replace: true },
    );
  };

  const handleSearchChange = (query: string) => {
    resetPaging();
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (query.trim()) {
          next.set("search", query);
        } else {
          next.delete("search");
        }
        return next;
      },
      { replace: true },
    );
  };

  const resetFilters = () => {
    resetPaging();
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("env");
        next.delete("branch");
        next.delete("search");
        return next;
      },
      { replace: true },
    );
  };

  const handleSort = (col: "date" | "build" | "status" | "new" | "fixed") => {
    const nextDir =
      sortCol === col
        ? sortDir === "ascending"
          ? "descending"
          : "ascending"
        : "descending";

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (col !== "date") {
          next.set("sort", col);
        } else {
          next.delete("sort");
        }
        if (nextDir !== "descending") {
          next.set("dir", nextDir);
        } else {
          next.delete("dir");
        }
        return next;
      },
      { replace: true },
    );
  };

  const filteredBuilds = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list = builds;
    if (q) {
      list = list.filter(
        (b) =>
          b.label?.toLowerCase().includes(q) ||
          b.environment?.toLowerCase().includes(q) ||
          b.gitBranch?.toLowerCase().includes(q) ||
          b.gitSha?.toLowerCase().includes(q),
      );
    }
    return [...list].sort((a, b) => {
      const sign = sortDir === "ascending" ? 1 : -1;
      if (sortCol === "build") {
        return (a.label || "").localeCompare(b.label || "") * sign;
      }
      if (sortCol === "status") {
        const stA = a.gate?.status ?? "";
        const stB = b.gate?.status ?? "";
        return stA.localeCompare(stB) * sign;
      }
      if (sortCol === "new") {
        const cntA =
          (a.gate?.counts.new.critical ?? 0) + (a.gate?.counts.new.major ?? 0);
        const cntB =
          (b.gate?.counts.new.critical ?? 0) + (b.gate?.counts.new.major ?? 0);
        return (cntA - cntB) * sign;
      }
      if (sortCol === "fixed") {
        const cntA =
          (a.gate?.counts.fixed.critical ?? 0) +
          (a.gate?.counts.fixed.major ?? 0);
        const cntB =
          (b.gate?.counts.fixed.critical ?? 0) +
          (b.gate?.counts.fixed.major ?? 0);
        return (cntA - cntB) * sign;
      }
      const timeA = a.lastRunAt ? new Date(a.lastRunAt).getTime() : 0;
      const timeB = b.lastRunAt ? new Date(b.lastRunAt).getTime() : 0;
      return (timeA - timeB) * sign;
    });
  }, [builds, searchQuery, sortCol, sortDir]);

  return (
    <Page
      title="Walkthroughs"
      description="Builds group the runs made against one version of your product and compare it with a baseline."
    >
      <WalkthroughsTabs projectId={project.id} current="builds" />
      {isLoading && builds.length === 0 ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : error ? (
        <EmptyState
          icon={AlertCircle}
          title="Failed to load builds"
          description="Something went wrong while fetching builds. Please try again."
          actions={
            <Button onClick={() => refetch()} variant="outline">
              Try again
            </Button>
          }
        />
      ) : builds.length === 0 && environment === ALL && branch === ALL ? (
        <EmptyState
          icon={Layers}
          title="No builds yet"
          description="Gates compare two versions of your product. Every pushed run joins a build: the label your CLI or CI sends with the run (iamu run --build 2.4.0), else one per commit, else one per day and environment."
          actions={
            <Button asChild variant="outline">
              <Link to={`/projects/${project.id}/settings?tab=quality-gate`}>
                Review quality gate settings
              </Link>
            </Button>
          }
        />
      ) : (
        <div className="space-y-4">
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            {/* Table Toolbar */}
            <TableToolbar
              variant="card"
              hasActiveFilters={hasActiveFilters}
              onResetFilters={resetFilters}
              count={
                <span>
                  <strong className="text-foreground">{builds.length}</strong>{" "}
                  build{builds.length !== 1 ? "s" : ""} evaluated
                </span>
              }
            >
              <TableSearchInput
                placeholder="Search builds or branches..."
                value={searchQuery}
                onChange={handleSearchChange}
                ariaLabel="Search builds"
              />
              {(data?.environments.length ?? 0) > 1 && (
                <FilterPill
                  ariaLabel="Filter by environment"
                  value={environment}
                  onValueChange={handleEnvironmentChange}
                  options={[
                    { value: ALL, label: "All environments" },
                    ...(data?.environments ?? []).map((e) => ({
                      value: e,
                      label: e,
                    })),
                  ]}
                />
              )}
              {(data?.branches.length ?? 0) > 0 && (
                <FilterPill
                  ariaLabel="Filter by branch"
                  value={branch}
                  onValueChange={handleBranchChange}
                  options={[
                    { value: ALL, label: "All branches" },
                    ...(data?.branches ?? []).map((b) => ({
                      value: b,
                      label: b,
                    })),
                  ]}
                />
              )}
            </TableToolbar>

            <BuildsTable
              projectId={project.id}
              builds={filteredBuilds}
              sortCol={sortCol}
              sortDir={sortDir}
              onSort={handleSort}
              onResetFilters={resetFilters}
            />

            {data?.nextCursor && (
              <div className="flex items-center justify-between border-t border-border/60 bg-muted/20 px-4 py-2.5">
                <span className="text-xs text-muted-foreground">
                  Showing {builds.length} builds
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setPages((p) => [
                      ...p,
                      data.items as unknown as BuildSummary[],
                    ]);
                    setCursor(data.nextCursor);
                  }}
                  className="h-7 text-xs"
                >
                  Show older builds
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </Page>
  );
}

function BuildsTable({
  projectId,
  builds,
  sortCol,
  sortDir,
  onSort,
  onResetFilters,
}: {
  projectId: string;
  builds: BuildSummary[];
  sortCol?: "date" | "build" | "status" | "new" | "fixed";
  sortDir?: "ascending" | "descending";
  onSort?: (col: "date" | "build" | "status" | "new" | "fixed") => void;
  onResetFilters?: () => void;
}) {
  if (builds.length === 0) {
    return (
      <FilteredEmptyState
        title="No builds match these filters"
        description="No builds match your environment, branch, or search criteria."
        onReset={onResetFilters}
        className="py-12"
      />
    );
  }
  return (
    <>
      {/* Mobile card list */}
      <div className="divide-y divide-border/60 lg:hidden">
        {builds.map((b) => {
          const gate = b.gate;
          const newSerious = gate
            ? gate.counts.new.critical + gate.counts.new.major
            : 0;
          return (
            <div key={b.id} className="p-4 space-y-2.5">
              <div className="flex items-center justify-between gap-2">
                <GateStatusChip status={gate?.status} />
                <span className="text-xs tabular-nums text-muted-foreground">
                  <Timestamp date={b.lastRunAt} />
                </span>
              </div>
              <div>
                <Link
                  to={`/projects/${projectId}/walkthroughs/builds/${b.id}`}
                  className="font-semibold text-foreground underline-offset-4 hover:underline text-sm"
                >
                  {b.label}
                </Link>
                {gate?.headline && (
                  <p className="mt-0.5 text-xs text-muted-foreground leading-relaxed">
                    {gate.headline.replace(
                      /^(inconclusive|passed with warnings|passed|failed|error):\s*/i,
                      "",
                    )}
                  </p>
                )}
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground tabular-nums">
                  <span>
                    {[
                      b.environment,
                      b.source !== "implicit"
                        ? b.source === "ci"
                          ? "CI"
                          : "CLI"
                        : null,
                      [b.gitBranch, shortSha(b.gitSha)]
                        .filter(Boolean)
                        .join(" "),
                      `${b.runCount} ${b.runCount === 1 ? "run" : "runs"}`,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                  {b.gitDirty && <UncommittedChip hash={b.worktreeHash} />}
                </div>
              </div>

              {gate && (
                <div className="grid grid-cols-4 gap-2 pt-2 border-t border-border/40 text-xs">
                  <div>
                    <div className="text-xs text-muted-foreground">New</div>
                    <div className="font-semibold tabular-nums mt-0.5">
                      {severityTotal(gate.counts.new)}
                      {newSerious > 0 && (
                        <span className="ml-1 text-xs text-severity-critical">
                          ({newSerious})
                        </span>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Fixed</div>
                    <div className="font-semibold tabular-nums mt-0.5 text-finding-helps">
                      {severityTotal(gate.counts.fixed)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">
                      Regressed
                    </div>
                    <div className="font-semibold tabular-nums mt-0.5 text-severity-critical">
                      {severityTotal(gate.counts.regressed)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">
                      Coverage
                    </div>
                    <div className="font-semibold tabular-nums mt-0.5">
                      {gate.coverage.scenarios.rerun}/
                      {gate.coverage.scenarios.in_scope}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Desktop table */}
      <div className="hidden lg:block overflow-x-auto no-scrollbar">
        <Table aria-label="Builds, newest first">
          <TableHeader className="bg-muted/30 border-b border-border/80">
            <TableRow className="hover:bg-transparent">
              <SortableTableHead
                col="status"
                label="Status"
                activeCol={sortCol}
                dir={sortDir}
                onSort={onSort ?? (() => {})}
                className="w-24 text-xs font-medium"
              />
              <SortableTableHead
                col="build"
                label="Build"
                activeCol={sortCol}
                dir={sortDir}
                onSort={onSort ?? (() => {})}
                className="text-xs font-medium"
              />
              <TableHead className="text-xs font-medium">Environment</TableHead>
              <TableHead className="hidden 2xl:table-cell text-xs font-medium">
                Baseline
              </TableHead>
              <SortableTableHead
                col="new"
                label="New"
                activeCol={sortCol}
                dir={sortDir}
                onSort={onSort ?? (() => {})}
                align="right"
                className="text-right text-xs font-medium"
              />
              <SortableTableHead
                col="fixed"
                label="Fixed"
                activeCol={sortCol}
                dir={sortDir}
                onSort={onSort ?? (() => {})}
                align="right"
                className="text-right text-xs font-medium"
              />
              <TableHead className="text-right text-xs font-medium">
                Regressed
              </TableHead>
              <TableHead className="text-right text-xs font-medium">
                Not re-checked
              </TableHead>
              <TableHead className="text-right text-xs font-medium">
                Coverage
              </TableHead>
              <SortableTableHead
                col="date"
                label="Last run"
                activeCol={sortCol}
                dir={sortDir}
                onSort={onSort ?? (() => {})}
                align="right"
                className="text-right text-xs font-medium"
              />
            </TableRow>
          </TableHeader>
          <TableBody>
            {builds.map((b) => {
              const gate = b.gate;
              const newSerious = gate
                ? gate.counts.new.critical + gate.counts.new.major
                : 0;
              return (
                <TableRow
                  key={b.id}
                  className="hover:bg-muted/40 transition-colors"
                >
                  <TableCell>
                    <GateStatusChip status={gate?.status} />
                  </TableCell>
                  <TableCell className="max-w-sm whitespace-normal">
                    <Link
                      to={`/projects/${projectId}/walkthroughs/builds/${b.id}`}
                      className="font-semibold text-foreground underline-offset-4 hover:underline"
                    >
                      {b.label}
                    </Link>
                    {gate?.headline && (
                      <p className="mt-0.5 text-xs text-muted-foreground whitespace-normal leading-relaxed">
                        {gate.headline.replace(
                          /^(inconclusive|passed with warnings|passed|failed|error):\s*/i,
                          "",
                        )}
                        {(gate.status === "inconclusive" ||
                          gate.status === "failed") && (
                          <>
                            {" · "}
                            <Link
                              to={`/projects/${projectId}/walkthroughs/builds/${b.id}`}
                              className="font-medium text-primary hover:underline whitespace-nowrap"
                            >
                              {gate.status === "inconclusive"
                                ? "See missing coverage →"
                                : "Review blockers →"}
                            </Link>
                          </>
                        )}
                      </p>
                    )}
                    <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground tabular-nums">
                      <span>
                        {[
                          b.source !== "implicit"
                            ? b.source === "ci"
                              ? "CI"
                              : "CLI"
                            : null,
                          [b.gitBranch, shortSha(b.gitSha)]
                            .filter(Boolean)
                            .join(" "),
                          `${b.runCount} ${b.runCount === 1 ? "run" : "runs"}`,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                      {b.gitDirty && <UncommittedChip hash={b.worktreeHash} />}
                    </div>
                  </TableCell>
                  <TableCell
                    className="max-w-56 truncate text-muted-foreground"
                    title={b.environment}
                  >
                    {b.environment}
                  </TableCell>
                  <TableCell className="hidden text-muted-foreground 2xl:table-cell">
                    {gate
                      ? (gate.baseline.label ??
                        BASELINE_MODE_LABELS[gate.baseline.mode] ??
                        gate.baseline.mode)
                      : "–"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {gate ? (
                      <>
                        {severityTotal(gate.counts.new)}
                        {newSerious > 0 && (
                          <span className="ml-1 text-severity-critical">
                            ({newSerious} critical/major)
                          </span>
                        )}
                      </>
                    ) : (
                      "–"
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {gate ? severityTotal(gate.counts.fixed) : "–"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {gate ? severityTotal(gate.counts.regressed) : "–"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {gate ? severityTotal(gate.counts.not_rechecked) : "–"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {gate
                      ? `${gate.coverage.scenarios.rerun}/${gate.coverage.scenarios.in_scope}`
                      : "–"}
                  </TableCell>
                  <TableCell className="text-right text-xs tabular-nums text-muted-foreground">
                    <Timestamp date={b.lastRunAt} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </>
  );
}
